import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import PostsIndex from "../../components/posts/_index";
import api from "../../api";

const IndexPosts = () => {
  const [posts, setPosts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 9;

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;

    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const fetchPosts = async (
    pageToLoad = 1,
    reset = false,
    category = selectedCategory
  ) => {
    try {
      pageToLoad === 1 ? setLoading(true) : setLoadingMore(true);
      setError("");

      const response = await api.get("/posts", {
        params: {
          page: pageToLoad,
          pageSize,
          ...(category ? { category } : {}),
        },
      });

      const newPosts = Array.isArray(response.data) ? response.data : [];

      setPosts((prevPosts) =>
        reset ? newPosts : [...prevPosts, ...newPosts]
      );

      setPage(pageToLoad);
      setHasMore(newPosts.length === pageSize);

      if (!category) {
        setAllCategories((prevCategories) => [
          ...new Set([
            ...prevCategories,
            ...newPosts.flatMap((post) => post.categories || []),
          ]),
        ]);
      }
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des articles.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPosts(1, true, null);
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPosts([]);
    setPage(1);
    setHasMore(true);
    fetchPosts(1, true, category);
  };

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;

    fetchPosts(page + 1, false, selectedCategory);
  };

  const pageTitle = selectedCategory
    ? `Articles ${selectedCategory} - LMFP Photography`
    : "Articles photo - LMFP Photography";

  const pageDescription = selectedCategory
    ? `Découvrez les articles photo de la catégorie ${selectedCategory} publiés par LMFP Photography.`
    : "Explorez les derniers articles photo de LMFP Photography : mariages, portraits, nature et reportages visuels.";

  const pageImage =
    posts.length > 0
      ? getImageUrl(posts[0]?.thumbnail || posts[0]?.image)
      : null;

  const pageUrl = selectedCategory
    ? `${window.location.origin}/posts?category=${selectedCategory}`
    : `${window.location.origin}/posts`;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900 text-white">
        <p className="text-xl font-semibold">Chargement...</p>
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <p className="mb-4">{error}</p>

          <button
            onClick={() => fetchPosts(1, true, selectedCategory)}
            className="rounded bg-orange-500 px-4 py-2 transition hover:bg-orange-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.lmfp-photography.com/posts" />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="LMFP Photography" />
        <meta property="og:locale" content="fr_FR" />
        {pageImage && <meta property="og:image" content={pageImage} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-white">
        <header className="p-6">
          <h1 className="mb-6 text-center text-4xl font-bold">Articles</h1>

          <div className="mx-auto max-w-5xl">
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`rounded-full border px-4 py-2 transition ${
                  selectedCategory === null
                    ? "border-orange-500 bg-orange-500 text-white"
                    : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                Toutes
              </button>

              {allCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`rounded-full border px-4 py-2 transition ${
                    selectedCategory === category
                      ? "border-orange-500 bg-orange-500 text-white"
                      : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 text-center text-gray-400">
            {posts.length} article(s) affiché(s)
          </div>
        </header>

        <main className="container mx-auto pb-8">
          {posts.length > 0 ? (
            <>
              <PostsIndex posts={posts} />

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center rounded-full bg-orange-500 px-6 py-3 text-sm font-medium text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loadingMore ? "Chargement..." : "Charger plus"}
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-6 text-center text-red-400">{error}</div>
              )}
            </>
          ) : (
            <div className="py-12 text-center text-gray-400">
              Aucun article trouvé pour cette catégorie.
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default IndexPosts;